'use client';
import ProductActions from '@components/ProductComparison/_ProductActions';
import CompareSection, { Props as SectionProps } from '@components/ProductComparison/_Section';
import Section from '@repo/ui/src/components/Section';
import React, { useState } from 'react';
import { Props as SeoProps } from '@repo/ui/src/components/Seo';
import { ImageProps } from '@repo/ui/src/types';
import Product from '@models/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import { ProductModelProps } from '@typings/product';
import useProductList from '@hooks/product/useProductList';

export interface ProductComparisonProps {
  sections?: Array<SectionProps>;
  skus: Array<string>;
  seo?: SeoProps;
  pageTitle: string;
  _testID: string;
  noSelectionImage: ImageProps;
  asContentRow?: boolean;
  _data?: Array<ProductModelProps>;
}

const ProductComparison = ({
  pageTitle,
  sections,
  skus,
  _testID,
  noSelectionImage,
  _data,
  asContentRow,
}: ProductComparisonProps) => {
  const mdAndUp = useIsMD();

  const { data } = useProductList({ skus });
  const products = (data ?? _data)?.map((product) => new Product(product)) ?? [];
  const getSkuList = (_skus: Array<string>) => {
    return asContentRow
      ? _skus.slice(0, 3)
      : !mdAndUp
        ? _skus.slice(0, 2)
        : [..._skus.slice(0, 2), ''];
  };

  const [selectedSkus, setSelectedSkus] = useState(getSkuList(skus));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => setSelectedSkus(getSkuList(selectedSkus)), [mdAndUp]);

  const HeadingTag = asContentRow ? 'h2' : 'h1';

  return (
    <Section texture>
      <div data-test-id={`hero-wrapper_${_testID}`} className="tw-py-10 md:tw-py-20 tw-text-center">
        <HeadingTag
          className={classNames(
            'tw-text-text-500',
            asContentRow ? 'tra-text-web-heading-2' : 'tra-text-web-heading-1',
          )}
        >
          {pageTitle}
        </HeadingTag>
      </div>

      <div
        className={classNames(
          'tw-bg-white md:tw-mx-10 md:tw-py-20 md:tw-px-10',
          asContentRow && 'tw-overflow-x-auto md:tw-overflow-x-visible',
        )}
      >
        <div className="tw-w-full">
          {!!products?.length && (
            <div>
              <ProductActions
                selectedSkus={selectedSkus}
                products={products}
                noSelectionImage={noSelectionImage}
                updateSelectedProducts={(sku, index) => {
                  setSelectedSkus(selectedSkus.map((oldSku, i) => (index === i ? sku : oldSku)));
                }}
                _testID={_testID}
                asContentRow={asContentRow}
              />

              {sections?.map((section, idx) => (
                <CompareSection
                  {...section}
                  key={idx}
                  selectedSkus={selectedSkus}
                  products={products}
                  asContentRow={asContentRow}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default ProductComparison;
